import React from 'react'
import { Button } from '../../components/ui'
import {
  SUPPORT_CENTER_MONTHS,
  SUPPORT_CENTER_WEEKDAYS,
  daysInMonth,
  toggleSortedNumber,
} from '../../components/centralAmparo/scheduleHelpers'
import { buildYearOptions, clampDay, type DateParts } from '../../components/centralAmparo/parametrosHelpers'

type WeekdayHourPickersProps = {
  weekdays: number[]
  hours: number[]
  onWeekdays: (next: number[]) => void
  onHours: (next: number[]) => void
}

export const WeekdayHourPickers: React.FC<WeekdayHourPickersProps> = ({
  weekdays,
  hours,
  onWeekdays,
  onHours,
}) => (
  <>
    <div>
      <div className="flex items-center justify-between mb-2">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          Dias da semana <span className="text-red-500">*</span>
        </label>
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => onWeekdays(SUPPORT_CENTER_WEEKDAYS.map((d) => d.value))}
          >
            Selecionar todos
          </Button>
          <Button size="sm" variant="ghost" onClick={() => onWeekdays([])}>
            Limpar
          </Button>
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        {SUPPORT_CENTER_WEEKDAYS.map((d) => (
          <label
            key={d.value}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 cursor-pointer select-none"
          >
            <input
              type="checkbox"
              checked={weekdays.includes(d.value)}
              onChange={() => onWeekdays(toggleSortedNumber(weekdays, d.value))}
            />
            <span className="text-sm text-gray-800 dark:text-gray-200">{d.label}</span>
          </label>
        ))}
      </div>
    </div>
    <div>
      <div className="flex items-center justify-between mb-2">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          Horas do dia <span className="text-red-500">*</span>
        </label>
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => onHours(Array.from({ length: 24 }, (_, i) => i))}
          >
            Selecionar todos
          </Button>
          <Button size="sm" variant="ghost" onClick={() => onHours([])}>
            Limpar
          </Button>
        </div>
      </div>
      <div className="grid grid-cols-6 sm:grid-cols-8 gap-2">
        {Array.from({ length: 24 }, (_, i) => i).map((h) => (
          <label
            key={h}
            className={`inline-flex items-center justify-center gap-2 px-2 py-1.5 rounded-md border text-sm cursor-pointer select-none ${
              hours.includes(h)
                ? 'border-primary-500 text-primary-700 dark:text-primary-300'
                : 'border-gray-300 dark:border-gray-600 text-gray-800 dark:text-gray-200'
            }`}
          >
            <input
              type="checkbox"
              checked={hours.includes(h)}
              onChange={() => onHours(toggleSortedNumber(hours, h))}
            />
            {h.toString().padStart(2, '0')}h
          </label>
        ))}
      </div>
    </div>
  </>
)

type DatePartSelectorsProps = {
  label: string
  parts: DateParts
  onChange: (next: DateParts) => void
  yearOptions?: number[]
}

export const DatePartSelectors: React.FC<DatePartSelectorsProps> = ({
  label,
  parts,
  onChange,
  yearOptions: yearOptionsProp,
}) => {
  const yearOptions = yearOptionsProp ?? buildYearOptions()
  const maxDay = daysInMonth(parts.year, parts.month)
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
        {label} <span className="text-red-500">*</span>
      </label>
      <div className="grid grid-cols-3 gap-2">
        <select
          value={parts.day}
          onChange={(e) => onChange(clampDay({ ...parts, day: Number(e.target.value) }))}
          className="rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
        >
          {Array.from({ length: maxDay }, (_, i) => i + 1).map((day) => (
            <option key={day} value={day}>
              {day}
            </option>
          ))}
        </select>
        <select
          value={parts.month}
          onChange={(e) => onChange(clampDay({ ...parts, month: Number(e.target.value) }))}
          className="rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
        >
          {SUPPORT_CENTER_MONTHS.map((m) => (
            <option key={m.value} value={m.value}>
              {m.label}
            </option>
          ))}
        </select>
        <select
          value={parts.year}
          onChange={(e) => onChange(clampDay({ ...parts, year: Number(e.target.value) }))}
          className="rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
        >
          {yearOptions.map((y) => (
            <option key={y} value={y}>
              {y}
            </option>
          ))}
        </select>
      </div>
    </div>
  )
}
